import {
  createTheme,
  defaultVariantColorsResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from "@mantine/core";

// Generated from axxalon brand teal #02e0dd, with shade 5 — the dark-mode
// primary — pulled to #54e6d6, the accent axxalon.com actually renders.
// Shades 6-9 are deliberately unchanged: shade 7 is the light-mode primary
// (see primaryShade below) and the homepage has no light mode to match.
const brand: MantineColorsTuple = [
  "#e6fffe",
  "#c8f9f7",
  "#a3f2ec",
  "#7cebe3",
  "#63e8dc",
  "#54e6d6",
  "#04bbb8",
  "#069896",
  "#087572",
  "#0a4d4b",
];

// Sampled from the computed styles on axxalon.com, mapped onto the slots
// Mantine v7 actually reads in dark mode. The order matters more than the
// names: each index below feeds a specific CSS variable, so moving a value
// changes a different part of the UI than you'd expect.
const dark: MantineColorsTuple = [
  "#f4f7fa", // 0 → --mantine-color-text            (headings on axxalon.com)
  "#c6ced9", // 1 → secondary text                  (body copy)
  "#9ba8b6", // 2 → --mantine-color-dimmed
  "#7d8b9a", // 3 → --mantine-color-placeholder
  "#2a3441", // 4 → --mantine-color-default-border  (their rgba(255,255,255,.08) over the card)
  "#171f29", // 5 → --mantine-color-default-hover   (their elevated surface)
  "#0f151c", // 6 → --mantine-color-default         (their card surface: Paper, Card, inputs)
  "#070a0f", // 7 → --mantine-color-body            (their page background)
  "#05080c", // 8
  "#03050a", // 9
];

/**
 * Gives filled primary surfaces a label colour that accounts for the active
 * colour scheme.
 *
 * Mantine picks a filled component's label via `getContrastColor`, which calls
 * `parseThemeColor` WITHOUT a `colorScheme` argument; that falls back to
 * `"light"`, so with a split `primaryShade` it always judges the light shade
 * (#069896, dark enough to keep a white label) and never the shade actually
 * painted in dark mode (#54e6d6, which leaves a white label at 1.5:1).
 *
 * `--mantine-primary-color-contrast` does not have this flaw — the default CSS
 * variables resolver emits it once per scheme from that scheme's own shade — so
 * deferring to it fixes Button, Badge, ActionIcon and ThemeIcon in one place.
 * Only primary-coloured filled surfaces are redirected; anything on an explicit
 * palette (a red destructive button, a green status badge) keeps Mantine's own
 * resolution, which is already correct for those.
 *
 * The comparison is against `primaryColor` rather than a missing `color`,
 * because components substitute `color || theme.primaryColor` before calling
 * the resolver — `color` is never undefined by the time it arrives here.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  return input.variant === "filled" && input.color === input.theme.primaryColor
    ? { ...resolved, color: "var(--mantine-primary-color-contrast)" }
    : resolved;
};

export const theme = createTheme({
  variantColorResolver,
  fontFamily: "Inter, system-ui, sans-serif",
  primaryColor: "brand",
  primaryShade: { light: 7, dark: 5 },
  defaultRadius: "sm",
  // The brand teal is light enough that Mantine's default white label on a
  // filled button lands at 1.5:1 — legible only if you already know what it
  // says. autoContrast flips the label to dark when the fill is light. Light
  // mode is unaffected: its primary is shade 7, dark enough to keep white.
  autoContrast: true,
  // The homepage CTA gradient, available as <Button variant="gradient">.
  defaultGradient: { from: "#2e86f0", to: "#54e6d6", deg: 120 },
  colors: {
    brand,
    dark,
  },
  components: {
    // Paper (and Card, which builds on it) defaults to --mantine-color-body,
    // i.e. exactly the page background — which read as a visible panel against
    // Mantine's stock #242424 but disappears completely against #070a0f, since
    // a shadow alone casts nothing on near-black. --mantine-color-default is
    // the surface token: white in light mode, dark-6 in dark, so this restores
    // the homepage's card-on-page separation without touching light mode.
    Paper: { defaultProps: { bg: "var(--mantine-color-default)" } },
    Card: { defaultProps: { bg: "var(--mantine-color-default)" } },
  },
});
